import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../contexts/AuthContext'
import { useUIPreferences, type DensityPreset } from '../../contexts/UIPreferencesContext'
import { User, Bell, Key, Shield, Globe, Palette, CreditCard, Loader2, ExternalLink, Check, Building2, AlertTriangle, SlidersHorizontal, KeyRound } from 'lucide-react'
import { BillingLoadError, type BillingLoadFailure } from './BillingDashboard'
import ExpertiseRegistration from '../../components/orchestrator/ExpertiseRegistration'
import ManageDevicesPanel from '../../components/disclosure/ManageDevicesPanel'
import MfaSettingsCard from '../../components/security/MfaSettingsCard'
import PageHeader from '../../components/ui/PageHeader'
import { STORAGE_KEYS } from '../../constants/config'
import { getSubscriptionStatus, getBillingBalance, getCustomerPortal, getMyPreferences, setMyPreference, type SubscriptionStatus, type BalanceInfo } from '../../services/api'
import type { EcosystemProfile } from '../../types'

// Region & Language — real, persisted personal preference keys (Plan §6). `timezone`
// and `locale` already carry platform defaults server-side; `date_format` is a
// personal-only key with no platform default yet, so it falls back to an AU-sensible
// client default until the user saves one.
const TIMEZONE_OPTIONS = [
  { value: 'Australia/Sydney', label: '(UTC+11:00) Australia/Sydney — AEDT' },
  { value: 'Australia/Melbourne', label: '(UTC+11:00) Australia/Melbourne — AEDT' },
  { value: 'Australia/Brisbane', label: '(UTC+10:00) Australia/Brisbane — AEST' },
  { value: 'Australia/Adelaide', label: '(UTC+10:30) Australia/Adelaide — ACDT' },
  { value: 'Australia/Perth', label: '(UTC+08:00) Australia/Perth — AWST' },
  { value: 'Pacific/Auckland', label: '(UTC+13:00) Pacific/Auckland — NZDT' },
  { value: 'UTC', label: '(UTC+00:00) Coordinated Universal Time' },
  { value: 'Europe/London', label: '(UTC+00:00) Europe/London' },
  { value: 'America/New_York', label: '(UTC-05:00) America/New York' },
  { value: 'Asia/Singapore', label: '(UTC+08:00) Asia/Singapore' },
]

const LOCALE_OPTIONS = [
  { value: 'en-AU', label: 'English (Australia)' },
  { value: 'en-US', label: 'English (United States)' },
  { value: 'en-GB', label: 'English (United Kingdom)' },
  { value: 'en-NZ', label: 'English (New Zealand)' },
]

const DATE_FORMAT_OPTIONS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (05/07/2026)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (07/05/2026)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2026-07-05)' },
]

function initialsOf(name?: string, email?: string): string {
  const source = (name || '').trim()
  if (source) {
    const parts = source.split(/\s+/).filter(Boolean)
    return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '?'
  }
  return (email || '?').slice(0, 1).toUpperCase()
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner', admin: 'Admin', editor: 'Editor', member: 'Member',
}

function passwordStrength(pw: string): { label: string; pct: number; tone: string } {
  if (!pw) return { label: '', pct: 0, tone: 'bg-gray-200' }
  let score = 0
  if (pw.length >= 12) score += 1
  if (pw.length >= 16) score += 1
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score += 1
  if (/[0-9]/.test(pw)) score += 1
  if (/[^A-Za-z0-9]/.test(pw)) score += 1
  if (score <= 1) return { label: 'Weak. Minimum 12 characters with an upper-case letter, a number and a symbol.', pct: 25, tone: 'bg-red-500' }
  if (score <= 3) return { label: 'Medium. Add length or another character type for a stronger password.', pct: 60, tone: 'bg-amber-500' }
  return { label: 'Strong. Minimum 12 characters with an upper-case letter, a number and a symbol.', pct: 100, tone: 'bg-emerald-500' }
}

// Notification preferences — persisted through the 4-layer preferences API.
// Unset defaults to "on" (matches the historical default-checked behaviour).
const NOTIFICATION_PREFS: { key: string; label: string }[] = [
  { key: 'notifications.review_requests', label: 'Review requests' },
  { key: 'notifications.shared_documents', label: 'Shared documents' },
  { key: 'notifications.connection_requests', label: 'Connection requests' },
  { key: 'notifications.system_announcements', label: 'System announcements' },
]

const DENSITY_OPTIONS: { value: DensityPreset; label: string; description: string }[] = [
  { value: 'comfortable', label: 'Comfortable', description: 'Default spacing, easy to read' },
  { value: 'compact', label: 'Compact', description: 'Reduced spacing, more content visible' },
  { value: 'dense', label: 'Dense', description: 'Minimal spacing, maximum information density' },
  { value: 'custom', label: 'Custom', description: 'Set your own spacing, font size, and radius' },
]

function SliderControl({ label, value, min, max, step, unit, onChange }: {
  label: string; value: number; min: number; max: number; step: number; unit: string
  onChange: (v: number) => void
}) {
  return (
    <div className="flex items-center gap-3">
      <label className="text-sm text-gray-600 w-28 shrink-0">{label}</label>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1 h-1.5 accent-iflow-500"
      />
      <span className="text-xs font-mono text-gray-500 w-14 text-right">{value}{unit}</span>
    </div>
  )
}

const TIER_LABELS: Record<string, string> = {
  trial: 'Trial', basic: 'Basic', inventor: 'Inventor',
  pro_inventor: 'Pro Inventor', unlimited: 'Unlimited',
}

const TIER_COLORS: Record<string, string> = {
  trial: 'bg-gray-100 text-gray-700', basic: 'bg-sky-100 text-sky-700',
  inventor: 'bg-blue-100 text-blue-700', pro_inventor: 'bg-purple-100 text-purple-700',
  unlimited: 'bg-emerald-100 text-emerald-700',
}

export default function Settings() {
  const { user } = useAuth()
  const { prefs, setDensity, setCustomValue } = useUIPreferences()
  const qc = useQueryClient()
  const [showEcosystemWizard, setShowEcosystemWizard] = useState(false)
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null)
  const [balance, setBalance] = useState<BalanceInfo | null>(null)
  const [subLoading, setSubLoading] = useState(true)
  const [portalError, setPortalError] = useState('')

  // Notification preferences — real persistence via the preferences API.
  const { data: prefData } = useQuery({ queryKey: ['my-preferences'], queryFn: getMyPreferences })
  const [savingPref, setSavingPref] = useState<string | null>(null)
  const notifPrefs = (prefData?.preferences || {}) as Record<string, unknown>
  const prefMut = useMutation({
    mutationFn: ({ key, value }: { key: string; value: boolean }) => setMyPreference(key, value),
    onMutate: ({ key }) => setSavingPref(key),
    onSuccess: (fresh) => qc.setQueryData(['my-preferences'], fresh),
    onSettled: () => setSavingPref(null),
  })

  // Region & Language — same resolved-preferences object as Notifications
  // above (timezone/locale already carry real platform defaults server-side).
  const regionPrefs = (prefData?.preferences || {}) as Record<string, unknown>
  const [timezone, setTimezone] = useState('Australia/Sydney')
  const [locale, setLocale] = useState('en-AU')
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY')
  const [regionSaved, setRegionSaved] = useState(false)
  useEffect(() => {
    if (!prefData) return
    if (typeof regionPrefs.timezone === 'string') setTimezone(regionPrefs.timezone)
    if (typeof regionPrefs.locale === 'string') setLocale(regionPrefs.locale)
    if (typeof regionPrefs.date_format === 'string') setDateFormat(regionPrefs.date_format)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefData])
  const regionMut = useMutation({
    mutationFn: async () => {
      const [, , fresh] = await Promise.all([
        setMyPreference('timezone', timezone),
        setMyPreference('locale', locale),
        setMyPreference('date_format', dateFormat),
      ])
      return fresh
    },
    onSuccess: (fresh) => { qc.setQueryData(['my-preferences'], fresh); setRegionSaved(true) },
  })

  // Organisation affiliation — real membership record for the active org
  // (falls back to the first membership, then the legacy `organization` string).
  const activeMembership = user?.org_memberships?.find((m) => m.org_id === user.active_org_id)
    || user?.org_memberships?.[0]

  // A personal user's "organisation" is a personal pseudo-org keyed by a raw id
  // (a UUID / Cognito sub, sometimes `personal-<id>`) with no human name. Showing
  // that raw id as an "Organisation affiliation" reads as a bug; label it honestly
  // as the personal workspace instead of surfacing the opaque identifier.
  const orgRaw = (activeMembership?.org_name || user?.organization || '').trim()
  const orgIsUnnamed = /^(personal-)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(orgRaw)
  const orgDisplayName = orgIsUnnamed ? 'Personal workspace' : orgRaw

  // Security — real Cognito self-service password change (Story #2252 fix: the
  // "Change Password" button was a permanent disabled stub with no backend call.
  // Cognito's ChangePassword operation is a genuine self-service API that needs
  // only the caller's own access token — the same direct-fetch pattern
  // AuthContext.login() already uses for InitiateAuth/RespondToAuthChallenge).
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwLoading, setPwLoading] = useState(false)
  const [pwError, setPwError] = useState('')
  const [pwSuccess, setPwSuccess] = useState(false)
  const strength = passwordStrength(newPassword)

  const handleChangePassword = async () => {
    setPwError('')
    setPwSuccess(false)
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPwError('Fill in all three fields.')
      return
    }
    if (newPassword !== confirmPassword) {
      setPwError('New password and confirmation do not match.')
      return
    }
    setPwLoading(true)
    try {
      const config = (window as { INNOVATEFLOW_CONFIG?: { REGION: string } }).INNOVATEFLOW_CONFIG
      const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || ''
      if (!config || !accessToken) throw new Error('Not signed in.')
      const resp = await fetch(`https://cognito-idp.${config.REGION}.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.ChangePassword',
        },
        body: JSON.stringify({
          AccessToken: accessToken,
          PreviousPassword: currentPassword,
          ProposedPassword: newPassword,
        }),
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.message || 'Unable to update your password.')
      setPwSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setPwError(err instanceof Error ? err.message : 'Unable to update your password.')
    } finally {
      setPwLoading(false)
    }
  }

  // UX-041: keep the failure instead of defaulting the plan to Trial.
  const [subFailure, setSubFailure] = useState<BillingLoadFailure | null>(null)
  const [balanceFailed, setBalanceFailed] = useState(false)
  const [billingAttempt, setBillingAttempt] = useState(0)

  useEffect(() => {
    const load = async () => {
      setSubLoading(true)
      setSubFailure(null)
      setBalanceFailed(false)
      try {
        const [sub, bal] = await Promise.all([
          getSubscriptionStatus().catch((e) => { setSubFailure({ error: e }); return null }),
          getBillingBalance().catch(() => { setBalanceFailed(true); return null }),
        ])
        if (sub) setSubscription(sub)
        if (bal) setBalance(bal)
      } finally {
        setSubLoading(false)
      }
    }
    load()
  }, [billingAttempt])

  const handleManageSubscription = async () => {
    setPortalError('')
    try {
      const { portal_url } = await getCustomerPortal()
      // P7 honesty: a click must produce a real outcome or an explained failure —
      // never nothing. Surface the true result instead of silently swallowing it.
      if (portal_url) window.open(portal_url, '_blank')
      else setPortalError('Stripe portal is not configured in this environment.')
    } catch {
      setPortalError('Unable to open the Stripe portal. Please try again or contact support.')
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader
        title="Account Settings"
        description="Your profile, subscription, appearance and notifications — how you appear to collaborators and how the app behaves for you."
        helpKey="account-settings"
      />

      {/* Profile */}
      <div className="light-card p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <User size={18} className="text-iflow-500" />
          <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
        </div>

        <div className="flex items-center gap-4 mb-5">
          <div className="w-14 h-14 rounded-full bg-iflow-600 text-white flex items-center justify-center text-lg font-semibold shrink-0">
            {initialsOf(user?.name, user?.email)}
          </div>
          <div>
            <button
              disabled
              title="Photo uploads aren't supported yet — your photo is managed by your identity provider."
              className="px-3 py-1.5 text-sm text-gray-400 border border-gray-200 rounded-lg cursor-not-allowed"
            >
              Change photo
            </button>
            <p className="text-xs text-gray-400 mt-1">Managed by your identity provider</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="profile-full-name" className="block text-sm font-medium text-gray-700 mb-1">Full name</label>
            <input
              id="profile-full-name"
              value={user?.name || ''}
              disabled
              readOnly
              placeholder="Not set"
              className="w-full px-3 py-2 light-input text-sm bg-sky-500/5 text-gray-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              Managed by your identity provider (Cognito) — ask an organisation admin to update it.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              value={user?.email || ''}
              readOnly
              className="w-full px-3 py-2 light-input text-sm bg-sky-500/5 text-gray-500"
            />
            <p className="text-xs mt-1 flex items-center gap-1">
              <span className="text-gray-400">Used to sign in.</span>
              {user?.email_verified ? (
                <span className="text-emerald-600 font-medium flex items-center gap-0.5">
                  <Check size={12} /> Verified
                </span>
              ) : (
                <span className="text-amber-600 font-medium flex items-center gap-0.5">
                  <AlertTriangle size={12} /> Not verified
                </span>
              )}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <input
              value={user?.role?.replace('-', ' ') || ''}
              readOnly
              className="w-full px-3 py-2 light-input text-sm bg-sky-500/5 text-gray-500 capitalize"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Organisation affiliation</label>
            {activeMembership || user?.organization ? (
              <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-sky-500/5 border border-gray-200">
                <span className="flex items-center gap-2 text-sm text-gray-800">
                  <Building2 size={14} className="text-gray-400" />
                  {orgDisplayName}
                </span>
                {activeMembership?.role && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-iflow-100 text-iflow-700 capitalize">
                    {ROLE_LABELS[activeMembership.role] || activeMembership.role}
                  </span>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 px-3 py-2">No organisation affiliation.</p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              Managed by your organisation. To leave, contact an org owner — it can&apos;t be changed here.
            </p>
          </div>
        </div>
      </div>

      {/* Region & Language */}
      <div className="light-card p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Globe size={18} className="text-iflow-500" />
          <h2 className="text-lg font-semibold text-gray-900">Region & Language</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="region-timezone" className="block text-sm font-medium text-gray-700 mb-1">Time zone</label>
            <select
              id="region-timezone"
              value={timezone}
              onChange={(e) => { setTimezone(e.target.value); setRegionSaved(false) }}
              className="w-full px-3 py-2 light-input text-sm"
            >
              {TIMEZONE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="region-locale" className="block text-sm font-medium text-gray-700 mb-1">Language & locale</label>
            <select
              id="region-locale"
              value={locale}
              onChange={(e) => { setLocale(e.target.value); setRegionSaved(false) }}
              className="w-full px-3 py-2 light-input text-sm"
            >
              {LOCALE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="region-date-format" className="block text-sm font-medium text-gray-700 mb-1">Date format</label>
            <select
              id="region-date-format"
              value={dateFormat}
              onChange={(e) => { setDateFormat(e.target.value); setRegionSaved(false) }}
              className="w-full px-3 py-2 light-input text-sm"
            >
              {DATE_FORMAT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">All dates and times across the app show in this zone.</p>
        <div className="flex items-center gap-2 mt-4">
          <button
            onClick={() => regionMut.mutate()}
            disabled={regionMut.isPending}
            className="px-4 py-2 text-sm text-white bg-iflow-600 rounded-lg hover:bg-iflow-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {regionMut.isPending && <Loader2 size={14} className="animate-spin" />}
            Save preferences
          </button>
          {regionSaved && !regionMut.isPending && (
            <span className="text-xs text-emerald-600 font-medium">✓ Saved</span>
          )}
          {regionMut.isError && (
            <span className="text-xs text-red-600">Failed to save — please try again.</span>
          )}
        </div>
      </div>

      {/* Subscription & Usage */}
      <div className="light-card p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard size={18} className="text-iflow-500" />
          <h2 className="text-lg font-semibold text-gray-900">Subscription & Usage</h2>
        </div>
        {subLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </div>
        ) : subFailure && !subscription ? (
          <BillingLoadError
            failure={subFailure}
            orgName={activeMembership?.org_name || user?.organization}
            onRetry={() => setBillingAttempt((n) => n + 1)}
          />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <div className="text-xs text-gray-500 mb-1">Plan</div>
                {subscription?.tier ? (
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    TIER_COLORS[subscription.tier] || TIER_COLORS.trial
                  }`}>
                    {TIER_LABELS[subscription.tier] || subscription.tier}
                  </span>
                ) : (
                  <span className="text-sm text-gray-500">Unavailable</span>
                )}
              </div>
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <div className="text-xs text-gray-500 mb-1">Status</div>
                <div className="text-sm font-medium text-gray-800 capitalize">
                  {(subscription?.subscription_status || 'active').replace('_', ' ')}
                </div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <div className="text-xs text-gray-500 mb-1">Credits Remaining</div>
                <div className="text-sm font-medium text-gray-800">
                  {balanceFailed ? 'Unavailable' : Number(balance?.remaining_usd) === -1 ? 'Unlimited' :
                    balance?.remaining_usd ? `$${parseFloat(balance.remaining_usd).toFixed(2)}` : '--'}
                  {balance?.monthly_credit_usd && Number(balance.monthly_credit_usd) !== -1 && (
                    <span className="text-xs text-gray-500 ml-1">/ ${balance.monthly_credit_usd}</span>
                  )}
                </div>
              </div>
            </div>
            {subscription?.trial_expires_at && subscription.tier === 'trial' && (
              <p className="text-xs text-amber-600">
                Trial expires: {new Date(subscription.trial_expires_at).toLocaleDateString()}
              </p>
            )}
            {subscription?.tier && subscription.tier !== 'trial' && subscription.tier !== 'unlimited' && (
              <div className="space-y-1">
                <button
                  onClick={handleManageSubscription}
                  className="inline-flex items-center gap-1 px-4 py-2 text-sm text-iflow-600 border border-iflow-300 rounded-lg hover:bg-iflow-50"
                >
                  Manage Subscription <ExternalLink className="w-3.5 h-3.5" />
                </button>
                {portalError && <p className="text-xs text-red-600">{portalError}</p>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Appearance & Density */}
      <div className="light-card p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Palette size={18} className="text-iflow-500" />
          <h2 className="text-lg font-semibold text-gray-900">Appearance</h2>
        </div>

        {/* Density presets */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          {DENSITY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setDensity(opt.value)}
              className={`p-3 rounded-lg border text-left transition-colors ${
                prefs.density === opt.value
                  ? 'border-iflow-500 bg-iflow-50 ring-1 ring-iflow-500'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="text-sm font-medium text-gray-900">{opt.label}</div>
              <div className="text-[11px] text-gray-500 mt-0.5">{opt.description}</div>
            </button>
          ))}
        </div>

        {/* Custom controls */}
        {prefs.density === 'custom' && (
          <div className="space-y-3 pt-3 border-t border-gray-100">
            <SliderControl label="Font size" value={prefs.fontSize} min={10} max={18} step={1} unit="px" onChange={(v) => setCustomValue('fontSize', v)} />
            <SliderControl label="Spacing" value={prefs.spacingUnit} min={4} max={24} step={1} unit="px" onChange={(v) => setCustomValue('spacingUnit', v)} />
            <SliderControl label="Line height" value={prefs.lineHeight} min={1.0} max={2.0} step={0.05} unit="" onChange={(v) => setCustomValue('lineHeight', v)} />
            <SliderControl label="Border radius" value={prefs.borderRadius} min={0} max={20} step={1} unit="px" onChange={(v) => setCustomValue('borderRadius', v)} />
          </div>
        )}

        {/* Live preview */}
        <div className="mt-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
          <div className="text-xs font-medium text-gray-500 mb-2">Preview</div>
          <div style={{
            fontSize: `${prefs.fontSize}px`,
            lineHeight: `${prefs.lineHeight}`,
            padding: `${Math.round(prefs.spacingUnit * 0.5)}px`,
            borderRadius: `${prefs.borderRadius}px`,
            border: '1px solid rgba(14,165,233,0.2)',
            background: 'rgba(255,255,255,0.8)',
          }}>
            <div style={{ fontWeight: 600, marginBottom: `${Math.round(prefs.spacingUnit * 0.25)}px` }}>Sample Card Title</div>
            <div style={{ color: '#6b7280' }}>This is how content will appear with your current density settings.</div>
          </div>
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="light-card p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Bell size={18} className="text-iflow-500" />
          <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
        </div>
        <div className="space-y-3">
          {NOTIFICATION_PREFS.map(({ key, label }) => {
            const isOn = notifPrefs[key] !== false // unset → on
            return (
              <label key={key} className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-700">{label}</span>
                <div className="flex items-center gap-2">
                  {savingPref === key && <Loader2 className="w-3.5 h-3.5 animate-spin text-iflow-500" />}
                  <input
                    type="checkbox"
                    checked={isOn}
                    disabled={savingPref === key}
                    onChange={() => prefMut.mutate({ key, value: !isOn })}
                    className="w-4 h-4 text-iflow-500 border-gray-300 rounded focus:ring-iflow-500 disabled:opacity-50"
                  />
                </div>
              </label>
            )
          })}
        </div>
      </div>

      {/* Security */}
      <div className="light-card p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield size={18} className="text-iflow-500" />
          <h2 className="text-lg font-semibold text-gray-900">Security</h2>
        </div>
        <p className="text-sm font-medium text-gray-800 mb-3">Password</p>
        <div className="space-y-3 max-w-md">
          <div>
            <label htmlFor="pw-current" className="block text-sm font-medium text-gray-700 mb-1">Current password</label>
            <input
              id="pw-current"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-3 py-2 light-input text-sm"
              autoComplete="current-password"
            />
          </div>
          <div>
            <label htmlFor="pw-new" className="block text-sm font-medium text-gray-700 mb-1">New password</label>
            <input
              id="pw-new"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 light-input text-sm"
              autoComplete="new-password"
            />
            {newPassword && (
              <>
                <div className="h-1.5 rounded-full bg-gray-100 mt-1.5 overflow-hidden">
                  <div className={`h-full ${strength.tone}`} style={{ width: `${strength.pct}%` }} />
                </div>
                <p className="text-xs text-gray-500 mt-1">{strength.label}</p>
              </>
            )}
          </div>
          <div>
            <label htmlFor="pw-confirm" className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
            <input
              id="pw-confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 light-input text-sm"
              autoComplete="new-password"
            />
          </div>
          {pwError && <p className="text-sm text-red-600">{pwError}</p>}
          {pwSuccess && <p className="text-sm text-emerald-600 font-medium">✓ Password updated successfully.</p>}
          <div className="flex items-center gap-2">
            <button
              onClick={handleChangePassword}
              disabled={pwLoading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-iflow-600 rounded-lg hover:bg-iflow-700 disabled:opacity-50"
            >
              {pwLoading && <Loader2 size={14} className="animate-spin" />}
              Update password
            </button>
            <button
              onClick={() => { setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setPwError(''); setPwSuccess(false) }}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
          <div className="bg-sky-500/5 border border-sky-200 rounded-lg p-3 flex items-start gap-2">
            <Shield size={14} className="text-iflow-500 mt-0.5 shrink-0" />
            <p className="text-xs text-gray-600">
              This updates your password with our identity provider (Cognito). Your current session stays
              signed in — other devices will need to sign in again the next time their session refreshes.
            </p>
          </div>
        </div>
      </div>

      {/* Two-factor authentication (MFA) — live status from GET /users/me */}
      <MfaSettingsCard />

      {/* Disclosure — Manage Devices */}
      <div className="light-card p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield size={18} className="text-iflow-500" />
          <h2 className="text-lg font-semibold text-gray-900">Disclosure</h2>
        </div>
        {user?.active_org_id ? (
          <ManageDevicesPanel orgId={user.active_org_id} />
        ) : (
          <p className="text-sm text-gray-500">No active organisation — disclosure devices are managed per-organisation.</p>
        )}
      </div>

      {/* Ecosystem Profile */}
      <div className="light-card p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Globe size={18} className="text-iflow-500" />
          <h2 className="text-lg font-semibold text-gray-900">Ecosystem Profile</h2>
        </div>
        <p className="text-sm text-gray-500 mb-3">Register your expertise to be discovered by inventors, investors, and collaborators in the ecosystem.</p>
        <button
          onClick={() => setShowEcosystemWizard(true)}
          className="px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800"
        >
          Set Up Ecosystem Profile
        </button>
      </div>

      <ExpertiseRegistration
        isOpen={showEcosystemWizard}
        onClose={() => setShowEcosystemWizard(false)}
        onComplete={(_profile: EcosystemProfile) => {
          setShowEcosystemWizard(false)
        }}
      />

      {/* API Key */}
      <div className="light-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <Key size={18} className="text-iflow-500" />
          <h2 className="text-lg font-semibold text-gray-900">API Access</h2>
        </div>
        <p className="text-sm text-gray-500 mb-3">Programmatic API access is coming soon.</p>
        <div className="flex items-center gap-2">
          <input
            value="••••••••••••••••"
            readOnly
            className="flex-1 px-3 py-2 light-input text-sm bg-sky-500/5 font-mono"
          />
          <button
            disabled
            title="API key management is coming soon"
            className="px-4 py-2 text-sm text-gray-400 border border-gray-200 rounded-lg cursor-not-allowed shrink-0"
          >
            Regenerate
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">Coming soon</p>
      </div>

      {/* Where Security & Keys and Preferences live.
          The sidebar's account line is the figures' three items — Account ·
          Billing · Sharing — and no figure anywhere draws an account menu to
          move the other two into. So they hang off Account, which IS the first
          item on that line: still two clicks from anywhere, and still in the
          nav resolver, rather than reachable only by typing a URL. */}
      <div className="light-card p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Key size={18} className="text-iflow-500" />
          <h2 className="text-lg font-semibold text-gray-900">More in your account</h2>
        </div>
        <ul className="space-y-2">
          <li>
            <Link to="/account/security" className="flex items-center gap-2 text-sm font-medium text-iflow-600 hover:text-iflow-700 hover:underline">
              <KeyRound size={15} aria-hidden /> Security &amp; Keys
            </Link>
            <p className="ml-6 text-xs text-gray-500">Your encryption keys and the devices that hold them.</p>
          </li>
          <li>
            <Link to="/account/preferences" className="flex items-center gap-2 text-sm font-medium text-iflow-600 hover:text-iflow-700 hover:underline">
              <SlidersHorizontal size={15} aria-hidden /> Preferences
            </Link>
            <p className="ml-6 text-xs text-gray-500">Defaults for how the platform behaves for you.</p>
          </li>
        </ul>
      </div>
    </div>
  )
}
